import type { TFunction } from 'i18next';
import type { ComponentType } from 'react';
import type { UserRole } from '../types';
import {
  LayoutDashboard,
  MessageSquare,
  Server,
  Settings,
  Megaphone,
  Activity,
  Users,
  Radio,
  Film,
  Package,
  CreditCard,
  UserPlus,
  Tag,
  Wifi,
  ServerCog,
  Tv2,
  Shield,
  History,
  Cpu,
  MonitorSpeaker,
  Clapperboard,
  Satellite,
  Cable,
  KeyRound,
  Globe2,
  Shuffle,
  ShieldCheck,
  FileCode2,
  Upload,
  FolderOpen,
  CalendarClock,
  ListVideo,
  LogIn,
  Eye,
  AlertTriangle,
  ShieldAlert,
  Timer,
  FolderSearch,
  Archive,
  ArrowLeftRight,
} from 'lucide-react';

export interface NavItem {
  label: string;
  Icon: ComponentType<{ size?: number }>;
  to: string;
}

export interface NavSection {
  // Stable key for remembering whether the group is folded.
  id?: string;
  title: string;
  items: NavItem[];
  defaultFolded?: boolean;
}

export function isClientAdmin(role: UserRole): boolean {
  return role === 'client';
}

export function isReseller(role: UserRole): boolean {
  return role === 'reseller' || role === 'subreseller';
}

export function getNavSections(role: UserRole, t: TFunction): NavSection[] {
  if (role === 'admin' || role === 'client') return clientAdminNav(t);
  if (role === 'subreseller') return subresellerNav(t);
  if (role === 'restreamer') return portalNav();
  return resellerNav(t);
}

function clientAdminNav(t: TFunction): NavSection[] {
  return [
    {
      id: 'overview',
      title: t('nav.overview'),
      items: [
        { label: t('nav.dashboard'), Icon: LayoutDashboard, to: '/' },
      ],
    },
    {
      id: 'live',
      title: t('nav.live', 'Live'),
      items: [
        { label: t('nav.streams'),       Icon: Radio,         to: '/streams' },
        { label: t('nav.channel_organizer', 'Organize'),   Icon: ArrowLeftRight, to: '/categories/organize' },
        { label: t('nav.live_m3u_sources', 'Playlist Sources'), Icon: ListVideo, to: '/streams/m3u-sources' },
        { label: t('originSources.title', 'Origin Sources'), Icon: Satellite, to: '/origin-sources' },
        { label: t('nav.lifecycle', 'Lifecycle'), Icon: Activity, to: '/lifecycle' },
        { label: t('providers.title', 'TV Providers'), Icon: Cable, to: '/providers' },
        { label: 'DRM Profiles',             Icon: Shield,        to: '/drm-profiles' },
        { label: 'EPG',                      Icon: Tv2,           to: '/epg' },
        { label: t('recordings.title', 'Recordings'), Icon: CalendarClock, to: '/recordings' },
      ],
    },
    {
      id: 'vod',
      title: t('nav.vod_group', 'VOD'),
      items: [
        { label: t('nav.movies', 'Movies'), Icon: Film,      to: '/vod' },
        { label: t('series.title', 'Series'), Icon: Clapperboard, to: '/series' },
        { label: t('nav.vod_m3u_sources', 'M3U Sources'), Icon: ListVideo, to: '/vod/m3u-sources' },
        { label: 'Watch Folders',            Icon: FolderOpen,    to: '/watch-folders' },
        { label: 'File Store',               Icon: Upload,        to: '/files' },
      ],
    },
    {
      id: 'customers',
      title: t('nav.customers', 'Customers'),
      items: [
        { label: t('subscribers.title'),  Icon: Users,    to: '/subscribers' },
        { label: t('packages.title'),     Icon: Package,  to: '/packages' },
        { label: t('connections.title'),  Icon: Wifi,     to: '/connections' },
        { label: t('nav.restreamers', 'Restreamers'), Icon: ServerCog, to: '/restreamers' },
        { label: 'Access Codes',          Icon: KeyRound, to: '/access-codes' },
      ],
    },
    {
      id: 'resellers',
      title: t('nav.resellers'),
      defaultFolded: true,
      items: [
        { label: t('resellers.title'), Icon: UserPlus,    to: '/resellers' },
        { label: 'Reseller Groups',    Icon: ShieldCheck, to: '/reseller-groups' },
      ],
    },
    {
      id: 'monitoring',
      title: t('nav.monitoring', 'Monitoring'),
      items: [
        { label: t('nav.health'),                Icon: Activity,       to: '/health' },
        { label: t('security.title', 'Security'), Icon: Shield,        to: '/security' },
        { label: t('mag.title', 'MAG Devices'),   Icon: MonitorSpeaker, to: '/mag' },
      ],
    },
    {
      id: 'logs',
      title: t('nav.logs', 'Logs'),
      items: [
        { label: 'Stream Events',      Icon: Activity,       to: '/logs/stream-events' },
        { label: 'Activity',           Icon: History,        to: '/logs/activity' },
        { label: 'Logins',             Icon: LogIn,          to: '/logs/logins' },
        { label: 'Watch History',      Icon: Eye,            to: '/logs/watch' },
        { label: 'Stream Errors',      Icon: AlertTriangle,  to: '/logs/stream-errors' },
        { label: 'Restream Detection', Icon: ShieldAlert,    to: '/logs/restream' },
        { label: 'Credits',            Icon: CreditCard,     to: '/logs/credits' },
        { label: 'MAG Events',         Icon: MonitorSpeaker, to: '/logs/mag-events' },
        { label: 'On-Demand Reaper',   Icon: Timer,          to: '/logs/ondemand' },
        { label: 'Watch Folder Scans', Icon: FolderSearch,   to: '/logs/folder-scans' },
        { label: 'Retention',          Icon: Archive,        to: '/logs/retention' },
      ],
    },
    {
      id: 'system',
      title: t('nav.system'),
      items: [
        { label: t('servers.title'),          Icon: Server,        to: '/servers' },
        { label: t('nav.settings'),           Icon: Settings,      to: '/settings' },
        { label: t('announcements.title'),    Icon: Megaphone,     to: '/announcements' },
        { label: t('tickets.title'),          Icon: MessageSquare, to: '/tickets' },
      ],
    },
    {
      id: 'setup',
      title: t('nav.setup', 'Setup'),
      defaultFolded: true,
      items: [
        { label: t('categories.title'),                  Icon: Tag,       to: '/categories' },
        { label: 'Output Devices',                       Icon: FileCode2, to: '/output-devices' },
        { label: t('transcoding.title', 'Transcoding'),  Icon: Cpu,       to: '/transcoding' },
        { label: t('proxyPool.title', 'Proxy Pool'),     Icon: Globe2,    to: '/proxy-pool' },
        { label: t('loadBalancer.title', 'Load Balancer'), Icon: Shuffle, to: '/load-balancer' },
      ],
    },
  ];
}

function resellerNav(t: TFunction): NavSection[] {
  return [
    {
      title: t('nav.overview'),
      items: [
        { label: t('nav.dashboard'), Icon: LayoutDashboard, to: '/' },
      ],
    },
    {
      title: t('nav.lines'),
      items: [
        { label: t('subscribers.title'), Icon: Users, to: '/subscribers' },
      ],
    },
    {
      title: t('nav.resellers'),
      items: [
        { label: t('resellers.title'), Icon: UserPlus, to: '/resellers' },
      ],
    },
    {
      title: t('nav.account'),
      items: [
        { label: t('nav.credits'),   Icon: CreditCard,    to: '/credits' },
        { label: t('announcements.title'), Icon: Megaphone, to: '/announcements' },
        { label: t('tickets.title'), Icon: MessageSquare, to: '/tickets' },
      ],
    },
  ];
}

function subresellerNav(t: TFunction): NavSection[] {
  return [
    {
      title: t('nav.overview'),
      items: [
        { label: t('nav.dashboard'), Icon: LayoutDashboard, to: '/' },
      ],
    },
    {
      title: t('nav.lines'),
      items: [
        { label: t('subscribers.title'), Icon: Users, to: '/subscribers' },
      ],
    },
    {
      title: t('nav.account'),
      items: [
        { label: t('announcements.title'), Icon: Megaphone, to: '/announcements' },
        { label: t('tickets.title'), Icon: MessageSquare, to: '/tickets' },
      ],
    },
  ];
}

// A restreamer's own portal: its line and nothing of the operator's panel.
function portalNav(): NavSection[] {
  return [
    {
      title: 'Your line',
      items: [
        { label: 'Overview', Icon: LayoutDashboard, to: '/portal' },
        { label: 'Links', Icon: Cable, to: '/portal/links' },
        { label: 'Channels', Icon: Tv2, to: '/portal/channels' },
        { label: 'Quality and ladder', Icon: Settings, to: '/portal/settings' },
        { label: 'Allowed IPs', Icon: Shield, to: '/portal/ips' },
      ],
    },
    {
      title: 'Support',
      items: [
        { label: 'Refused requests', Icon: AlertTriangle, to: '/portal/refusals' },
        { label: 'API keys', Icon: KeyRound, to: '/portal/keys' },
        { label: 'Activity', Icon: History, to: '/portal/activity' },
      ],
    },
    {
      title: 'Account',
      items: [
        { label: 'Two-factor', Icon: ShieldCheck, to: '/settings/2fa' },
        { label: 'Change password', Icon: LogIn, to: '/change-password' },
      ],
    },
  ];
}
